/**
 * /app/screeners/:id — страница скринера (PULSE-268 ч.2).
 *
 * Параметры (для universal — три метрики, для signal — фильтры) →
 * «Бектест 7 дней» → список событий + график вокруг выбранного события
 * (MarketChart: цена/OI/дельта/ликвидации, маркер в момент сигнала,
 * 2/3 окна — что было с монетой ДАЛЬШЕ). Переключение «назад/вперёд».
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, FlaskConical, Loader2 } from 'lucide-react'

import { EventViewer, eventSummary } from '@/components/screeners/EventViewer'
import {
  Field, INPUT, MetricBlocks, metricFormsFromParams, metricsToParams,
  type MetricForms,
} from '@/components/screeners/metricForm'
import {
  type BacktestEvent,
  type ScreenerDef,
  type UniversalParams,
  useScreenerBacktest, useScreenersOverview,
} from '@/hooks/useScreenersApi'
import { cn } from '@/lib/cn'

function SignalFilterForm({ value, onChange }: {
  value: { direction: string; symbol: string; min_score: string }
  onChange: (v: { direction: string; symbol: string; min_score: string }) => void
}) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <Field label="Направление">
        <select
          value={value.direction}
          onChange={(e) => onChange({ ...value, direction: e.target.value })}
          className={INPUT}
        >
          <option value="">любое</option>
          <option value="long">long</option>
          <option value="short">short</option>
        </select>
      </Field>
      <Field label="Символ">
        <input
          value={value.symbol}
          onChange={(e) => onChange({ ...value, symbol: e.target.value })}
          className={INPUT} placeholder="любой"
        />
      </Field>
      <Field label="Мин. score">
        <input
          value={value.min_score}
          onChange={(e) => onChange({ ...value, min_score: e.target.value })}
          className={INPUT} placeholder="—" inputMode="numeric"
        />
      </Field>
    </div>
  )
}


export default function ScreenerDetail() {
  const { id } = useParams()
  const defId = Number(id)
  const { data: overview, isLoading: ovLoading } = useScreenersOverview()
  const backtest = useScreenerBacktest()

  const def: ScreenerDef | null = useMemo(
    () => overview?.available.find((a) => a.def.id === defId)?.def ?? null,
    [overview, defId])

  // PULSE-372: ?params=<json> — переход «Бектест» со страницы подбора сетапов
  const [search] = useSearchParams()
  const urlParams = useMemo(() => {
    try {
      const raw = search.get('params')
      return raw ? (JSON.parse(raw) as UniversalParams) : undefined
    } catch { return undefined }
  }, [search])

  const myParams = useMemo(() => {
    const inst = overview?.available.find((a) => a.def.id === defId)?.instances?.[0]
    return inst?.params
  }, [overview, defId])

  const [metrics, setMetrics] = useState<MetricForms | null>(null)
  const [sigFilters, setSigFilters] = useState({ direction: '', symbol: '', min_score: '' })
  const [refire, setRefire] = useState('60')
  const [err, setErr] = useState<string | null>(null)
  const [events, setEvents] = useState<BacktestEvent[] | null>(null)
  const [index, setIndex] = useState(0)

  // параметры: из моего инстанса, иначе — дефолты скринера
  useEffect(() => {
    if (def && def.kind === 'universal' && metrics === null) {
      setMetrics(metricFormsFromParams(urlParams ??
        ((myParams && (myParams.price || myParams.oi || myParams.nd || myParams.gap))
          ? myParams : def.default_params)))
    }
  }, [def, myParams, urlParams, metrics])

  async function run() {
    if (!def) return
    setErr(null)
    let params: Record<string, unknown> = {}
    if (def.kind === 'universal') {
      if (!metrics) return
      const p = metricsToParams(metrics)
      if (typeof p === 'string') { setErr(p); return }
      params = p
    } else {
      if (sigFilters.direction) params.direction = sigFilters.direction
      if (sigFilters.symbol.trim()) params.symbol = sigFilters.symbol.trim().toUpperCase()
      if (sigFilters.min_score.trim() && !Number.isNaN(Number(sigFilters.min_score))) {
        params.min_score = Number(sigFilters.min_score)
      }
    }
    const rf = Number(refire)
    try {
      const r = await backtest.mutateAsync({
        screenerId: def.id, params,
        refireMin: Number.isInteger(rf) && rf >= 1 && rf <= 1440 ? rf : 60,
      })
      setEvents(r.events)
      setIndex(0)
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErr(detail ?? 'Бектест не удался (таймаут?). Сузьте параметры.')
    }
  }

  if (ovLoading) {
    return <div className="p-6 text-textFaint text-sm">…</div>
  }
  if (!def) {
    return (
      <div className="p-6">
        <p className="text-sm text-textDim">Скринер не найден или недоступен.</p>
        <Link to="/app/screeners" className="text-sm text-fire hover:underline">← к скринерам</Link>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4">
        <Link to="/app/screeners" className="inline-flex items-center gap-1 text-xs text-textFaint hover:text-textDim mb-2">
          <ArrowLeft size={13} /> Скринеры
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">{def.name}</h1>
        <p className="text-sm text-textDim mt-1 max-w-3xl">{def.description}</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 items-start">
        {/* ── Параметры + запуск ── */}
        <div className="space-y-3">
          <div className="bg-ink border border-line p-4 space-y-3">
            {def.kind === 'universal' && metrics && (
              <MetricBlocks value={metrics} onChange={setMetrics} />
            )}
            {def.kind === 'signal' && (
              <SignalFilterForm value={sigFilters} onChange={setSigFilters} />
            )}
            {def.kind === 'universal' && (
              <Field label="Мин. пауза между событиями одной монеты, мин"
                hint="Разделение эпизодов: непрерывное выполнение условий считается одним событием.">
                <input
                  value={refire}
                  onChange={(e) => setRefire(e.target.value)}
                  className={INPUT} inputMode="numeric"
                />
              </Field>
            )}
            {err && <div className="text-rose text-sm">{err}</div>}
            <button
              onClick={run}
              disabled={backtest.isPending}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-fire/15 text-fire border border-fire/30 hover:bg-fire/25 disabled:opacity-50"
            >
              {backtest.isPending
                ? <><Loader2 size={14} className="animate-spin" /> считаем…</>
                : <><FlaskConical size={14} /> Бектест: последние 7 дней</>}
            </button>
          </div>

          {/* ── Список событий ── */}
          {events && (
            <div className="bg-ink border border-line">
              <div className="px-3 py-2 border-b border-line text-xs text-textDim">
                Событий: <b className="text-text">{events.length}</b>
                {events.length >= 400 && ' (показаны последние 400)'}
              </div>
              {events.length === 0 && (
                <div className="px-3 py-6 text-center text-textFaint text-sm">
                  За 7 дней ни одного срабатывания. Ослабьте пороги.
                </div>
              )}
              <div className="max-h-[420px] overflow-y-auto divide-y divide-line/40">
                {events.map((e, i) => (
                  <button
                    key={`${e.symbol}-${e.ts}`}
                    onClick={() => setIndex(i)}
                    className={cn('w-full text-left px-3 py-2 hover:bg-line/20',
                      i === index && 'bg-fire-faint')}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{e.symbol}</span>
                      <span className="text-2xs text-textFaint ml-auto tabular-nums">
                        {new Date(e.ts).toLocaleString('ru-RU', {
                          day: '2-digit', month: '2-digit',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <div className="text-2xs text-textDim mt-0.5">{eventSummary(e)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── График события ── */}
        <div className="min-w-0">
          {!events && (
            <div className="bg-ink border border-line px-5 py-16 text-center text-textFaint text-sm">
              Задайте параметры и запустите бектест — здесь появятся события
              с графиком: момент сигнала и что было с монетой дальше.
              Переключение: кнопки «новее/старее» или стрелки ←/→.
            </div>
          )}
          {events && events.length > 0 && (
            <EventViewer events={events} index={index} setIndex={setIndex} />
          )}
        </div>
      </div>
    </div>
  )
}
